import * as THREE from 'three';
import { EngineDemoApp } from '../engine/apps/EngineDemoApp';
import { LoadingPhase } from '../engine/core/LoadingManager';
import { InputEventManager } from '../engine/core/InputEventManager';
import { UIUpdateManager } from '../ui/UIUpdateManager';
import { AdvancedPOICamera } from '../engine/camera/AdvancedPOICamera';
import { FlyCamera } from '../engine/cameras/FlyCamera';
import { POI } from '../engine/poi/POI';
import { POIData } from '../engine/poi/POIData';
import { Color, Vector3, TextRenderMode } from '../engine/types';
import { LabelParentMode } from '../engine/core/AppSettingsData';
import { fetchKoralmContent, type KoralmContentItem } from './contentService';
import { AssetLoadingBus } from '../engine/core/AssetLoadingBus';
import { TextureLodManager } from '../engine/textures/TextureLodManager.ts';

interface KoralmAppOptions {
  contentLimit?: number;
  categories?: string[];
}

/**
 * KoralmApp
 *
 * A data-driven variant of the EngineDemoApp that pulls Koralm news/articles
 * directly from the API and creates POIs without the markdown preprocessing step.
 */
export class KoralmApp extends EngineDemoApp {
  private readonly options: KoralmAppOptions;

  constructor(canvas: HTMLCanvasElement, cssContainer: HTMLElement, options: KoralmAppOptions = {}) {
    super(canvas, cssContainer);
    this.suppressDemoLoad = true;
    this.options = options;


    this.appSettings.hidePinSourceMeshes.value = false;
    this.appSettings.cameraDistance.value = 0.07;
    this.appSettings.enableBillboardForMapAnchors.value = false;
    this.appSettings.shapeBillboardYawOnly.value = false;
    this.sceneSettings.videoAutoplay.value = true;

    this.appSettings.labelRenderMode.value = TextRenderMode.SCREEN_HTML_STATIC;
    this.appSettings.labelScale.value = 0.01;
    this.appSettings.labelParentMode.value = LabelParentMode.POISHAPE;
    this.appSettings.fadeLabelOnActive.value = false;
    this.appSettings.labelDistance.value = 0.08;
    this.appSettings.labelLongitude.value = 0;
    this.appSettings.labelLatitude.value = 0;
    this.appSettings.labelColor.value = new Color('#FFFFFF');
    this.appSettings.labelBackgroundColor.value = new Color('#00000066');
    this.appSettings.labelMaxWidth.value = 500;
    this.appSettings.labelBillboardMode.value = false;
    this.appSettings.showConnector.value = false;
    this.appSettings.connectorColor.value = new Color('#ffffff');
    this.appSettings.connectorThickness.value = 1;

    this.sceneSettings.enableBillboard.value = true;
    TextureLodManager.Instance.setMaxConcurrent(4);
  }

  protected override async onInitialize(): Promise<void> {
    if (!this.scene) {
      this.addError('Scene not initialized');
      return;
    }

    this.loadingManager.startPhase(LoadingPhase.SceneSetup, '🎪 Setting up 3D scene…');
    this.loadingManager.addTask('input-setup', 'Input Event Manager', 1);
    this.loadingManager.addTask('ui-manager', 'UI Manager', 1);

    if (this.canvas) {
      InputEventManager.Instance.setCanvas(this.canvas);
      InputEventManager.Instance.setScene(this.scene.getThreeScene());
      InputEventManager.Instance.setCamera(
        this.scene.getThreeScene().children.find((obj) => obj instanceof THREE.Camera) as THREE.Camera,
      );

      this.canvas.addEventListener(
        'wheel',
        (event) => {
          event.preventDefault();
          if (this.appSettings?.cameraDistance) {
            const currentDistance = this.appSettings.cameraDistance.value ?? 3.8;
            const baseZoomSpeed = 0.1;
            const maxZoomPerTurn = 2.0;
            let zoomFactor = -event.deltaY * baseZoomSpeed * Math.min(currentDistance * 0.1, maxZoomPerTurn);
            if (event.deltaY > 0) {
              zoomFactor *= 0.5;
            }
            const newDistance = currentDistance + zoomFactor;
            const minDistance = this.appSettings.cameraDistance.boundsMin ?? 0.2;
            const maxDistance = this.appSettings.cameraDistance.boundsMax ?? 20;
            const clamped = Math.max(minDistance, Math.min(maxDistance, newDistance));
            if (Math.abs(clamped - currentDistance) > 0.001) {
              this.appSettings.cameraDistance.value = clamped;
            }
          }
        },
        { passive: false },
      );

      this.canvas.tabIndex = 0;
      this.canvas.style.outline = 'none';
      this.canvas.addEventListener('click', () => this.canvas?.focus());
      this.canvas.focus();
    }
    this.loadingManager.completeTask('input-setup');

    this.sceneManager?.addSceneNode(new UIUpdateManager());
    this.loadingManager.completeTask('ui-manager');
    this.loadingManager.completePhase();

    this.loadingManager.startPhase(LoadingPhase.CameraSetup, '🎥 Setting up camera system…');
    this.loadingManager.addTask('camera-creation', 'Advanced POI Camera', 1);
    this.loadingManager.addTask('ar-setup', 'AR System Setup', 1);

    this.camera = new AdvancedPOICamera(this.scene.getThreeScene(), this.appSettings, this.systemState);
    this.scene.setCamera(this.camera.internalCamera);
    InputEventManager.Instance.setCamera(this.camera.internalCamera);
    this.loadingManager.completeTask('camera-creation');

    const flyCamera = new FlyCamera('SpectatorCamera', this.appSettings);
    (this as any).flyCamera = flyCamera;
    this.cameraManager.addCamera(flyCamera);
    if (this.camera?.internalCamera) {
      flyCamera.setRenderCamera(this.camera.internalCamera);
    }
    this.arManager.initialize(
      this.scene.getRenderer(),
      this.scene.getThreeScene(),
      this.camera.internalCamera,
      this.camera,
    );
    this.loadingManager.completeTask('ar-setup');
    this.loadingManager.completePhase();

    this.setupLayoutSystem();
    this.configureKoralmBackgroundGradient();

    this.loadingManager.startPhase(LoadingPhase.POILoading, '📰 Loading Koralm content…');
    this.loadingManager.addTask('fetch-koralm', 'Fetch articles', 2);
    this.loadingManager.addTask('create-pois', 'Create POIs', 3);

    try {
      const items = await fetchKoralmContent({
        limit: this.options.contentLimit ?? 1000,
        categories: this.options.categories,
      });
      this.loadingManager.updateTask('fetch-koralm', 100, `${items.length} articles`);

      this.clearAllPOIs();

      const pois = items.map((item, index) => this.createPoiFromKoralmItem(item, index));
      pois.forEach((poi) => this.addPOI(poi));

      if (this.pois.length > 0) {
        this.setActivePOI(this.pois[0]);
      }

      this.loadingManager.updateTask('create-pois', 100, `${pois.length} POIs`);
      console.info('[KoralmApp] Created POIs', { count: pois.length });
    } catch (error) {
      console.error('[KoralmApp] Failed to fetch Koralm content', error);
      this.loadingManager.updateTask('fetch-koralm', 0, 'Failed to load');
    }

    this.loadingManager.completeTask('fetch-koralm');
    this.loadingManager.completeTask('create-pois');
    this.loadingManager.completePhase();

    this.loadingManager.startPhase(LoadingPhase.MaterialLoading, '🎨 Loading textures and videos…');
    this.loadingManager.addTask('material-registration', 'Register materials for loading', 1);
    this.loadingManager.addTask('material-loading', 'Load all materials', 3);

    await this.registerAllMaterialsForLoading();
    this.loadingManager.completeTask('material-registration');

    await this.waitForAllMaterialsToLoad();
    TextureLodManager.Instance.startQueuedUpgrades();
    this.loadingManager.completeTask('material-loading');
    this.loadingManager.completePhase();

    this.configureLayout();

    this.loadingManager.startPhase(LoadingPhase.Ready, '🚀 Preparing to launch…');
    this.loadingManager.addTask('final-setup', 'Final preparations', 1);
    await new Promise((resolve) => setTimeout(resolve, 300));
    this.loadingManager.completeTask('final-setup');
    this.loadingManager.complete();

    await this.loadingManager.waitUntilReady();
  }

  private createPoiFromKoralmItem(item: KoralmContentItem, index: number): POI {
    const data = new POIData(this.sceneSettings, this.appSettings);
    data.shape.value = 'tile';
    data.scale.value = 0.95;
    data.depth.value = 0.12;
    data.cornerRadius.value = 0.08;
    data.edgeRadius.value = 0.05;
    data.showLabel.value = true;
    data.labelRenderMode.value = TextRenderMode.SCREEN_HTML_STATIC;
    data.labelFontSize.value = 20;
    data.labelColor.value = new Color('#ffffff');
    data.labelBackgroundColor.value = new Color('#FF0000b3');
    data.labelDistance.value = 0.2;
    data.labelScale.value = 1.0;
    data.labelMaxWidth.value = 520;
    data.showConnector.value = false;

    const previewUrl = item.previewImageUrl ?? item.imageUrl ?? item.fullImageUrl ?? null;
    const fullUrl = item.fullImageUrl ?? item.imageUrl ?? previewUrl;
    if (previewUrl) {
      data.uri.value = [previewUrl];
    } else if (fullUrl) {
      data.uri.value = [fullUrl];
    }

    const markdownLines = [`# ${item.title}`];
    if (item.summary) {
      markdownLines.push('', item.summary);
    }
    if (item.sourceUrl) {
      markdownLines.push('', `[Mehr erfahren](${item.sourceUrl})`);
    }
    if (item.category) {
      markdownLines.push('', `**Kategorie:** ${item.category}`);
    }
    if (item.publishedAt) {
      markdownLines.push('', `**Veröffentlicht:** ${item.publishedAt}`);
    }
    data.markdown.value = markdownLines.join('\n');

    if (item.accentColor) {
      try {
        data.color.value = new Color(item.accentColor);
      } catch {
        data.color.value = new Color('#ffffff');
      }
    } else {
      data.color.value = new Color('#ffffff');
    }

    data.localTranslationOffset.value = new Vector3(0, 0, 0);

    const poiName = `${item.title || 'Koralm Item'}-${index}`;
    const layoutData = this.getLayoutData();
    const poi = new POI(poiName, data, this.sceneSettings, this.appSettings, layoutData);
    TextureLodManager.Instance.registerPoi(poi, previewUrl, fullUrl);
    return poi;
  }

  private configureLayout(): void {
    const layoutData = this.getLayoutData();
    layoutData.fieldAwarenessEnabled.value = false;
    this.setLayouter('line');
    try {
      if (layoutData.lineFixedSpacing) {
        layoutData.lineFixedSpacing.value = 2.0;
      }
      if (layoutData.lineCenterLayout) {
        layoutData.lineCenterLayout.value = true;
      }
      if (layoutData.lineDirection) {
        layoutData.lineDirection.value = new Vector3(1, 0, 0);
      }
    } catch (error) {
      console.warn('[KoralmApp] Failed to configure line layouter', error);
    }

    this.applyLayoutWithProcessCurveUpdate();

    this.setupKoralmAssetObservers();
  }

  private configureKoralmBackgroundGradient(): void {
    if (!this.sceneSettings) {
      return;
    }
    try {
      this.sceneSettings.backgroundGradientColor1.value = new Color('#000000');
      this.sceneSettings.backgroundGradientColor2.value = new Color('#1a1a1a');
      this.sceneSettings.backgroundGradientAngle.value = 90;
    } catch (error) {
      console.warn('[KoralmApp] Failed to set background gradient', error);
    }
  }

  private setupKoralmAssetObservers(): void {
    let pendingAssets = 0;
    const onStart = () => {
      pendingAssets += 1;
      this.loadingManager.updateTask('material-loading', Math.min(95, pendingAssets * 5));
    };
    const onComplete = () => {
      pendingAssets = Math.max(0, pendingAssets - 1);
      if (pendingAssets === 0) {
        this.loadingManager.updateTask('material-loading', 100);
      }
    };

    AssetLoadingBus.on('asset:load-start', onStart);
    AssetLoadingBus.on('asset:load-complete', onComplete);
    AssetLoadingBus.on('asset:load-error', onComplete);
  }
}

